import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ProjectFormInitialization, ProjectFormsService } from "@components/project-forms/project-forms.service";
import { UserService } from "@domain/user/user.service";
import { AreaHeaderComponent } from "@odx/angular/components/area-header";
import { FormFieldComponent, FormGroupComponent } from "@odx/angular/components/form-field";
import { map, Subject, takeUntil } from "rxjs";

@Component({
  selector: "app-customer-form",
  templateUrl: "./customer-form.component.html",
  styleUrls: ["./customer-form.component.scss"],
  imports: [UiKitModule, ReactiveFormsModule, AreaHeaderComponent, FormFieldComponent, FormGroupComponent],
  standalone: true,
})
export class CustomerFormComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private projectFormsService = inject(ProjectFormsService);
  private userService = inject(UserService);

  isReadonly: InputSignal<boolean> = input.required<boolean>();

  @Output() customerFormEmitter$ = new EventEmitter<FormGroup>();
  customerForm!: FormGroup;

  @ViewChild("submitButton")
  submitButton?: ElementRef;

  private readonly _destroying = new Subject<void>();
  private isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
  isReadOnlyUser = toSignal(this.isReadOnlyUser$);
  isWriteable = signal<boolean>(false);
  existingProject = input(false);

  constructor() {
    effect(() => {
      this.isReadonly();
      this.isReadOnlyUser();
      const result = !this.isReadOnlyUser() && !this.isReadonly();
      this.isWriteable.update(() => result);
    });

    this.customerForm = this.formBuilder.group({
      name: [null, Validators.required],
      customerNumber: [],
      street: [],
      zipCode: [],
      city: [],
    });
    this.projectFormsService.projectInitialized$.pipe(takeUntil(this._destroying)).subscribe((project) => {
      if (!project) return;
      this.initForm(project);
    });

    this.projectFormsService.projectReset$.pipe(takeUntil(this._destroying)).subscribe((project) => {
      this.customerForm.reset();
      this.initForm(project);
    });

    this.customerForm.statusChanges
      .pipe(takeUntil(this._destroying))
      .subscribe(() => this.customerFormEmitter$.emit(this.customerForm));
  }

  ngOnInit(): void {
    this.customerFormEmitter$.emit(this.customerForm);
  }

  ngOnDestroy(): void {
    this._destroying.next();
    this._destroying.complete();
  }

  initForm(project: ProjectFormInitialization) {
    this.customerForm.patchValue({
      name: project.customer?.name,
      customerNumber: project.customer?.customerNumber,
      street: project.customer?.street,
      zipCode: project.customer?.zipCode,
      city: project.customer?.city,
    });
  }

  triggerSubmit() {
    this.submitButton?.nativeElement.click();
  }

  protected readonly navigator = navigator;
}
